import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import reports from "../../../apiConfigs/reports";
import { ReactComponent as Delete } from "../../../assets/delete.svg";
import { ReactComponent as Edit } from "../../../assets/edit.svg";
import { useRoot } from "../../../context/RootProvider";
import DeleteModal from "../../../designComponents/DeleteModal";
import LabelInputField from "../../../designComponents/LabelInputField/LabelInputField";
import {
  renderFullDetails,
  RenderImages,
  renderstatus,
} from "../../../designComponents/Table/renderMethods";
import Typography from "../../../designComponents/Typography";
import { humanReadableDateWithTime } from "../../../utils/date";
import requestAPI from "../../../utils/requestAPI";
import { resizeImage } from "../../../utils/resizeImages";
import textHelper from "../../../utils/textHelper";
import {
  AcceptButton,
  ButtonsSection,
  Card,
  LabelText,
  RejectButton,
  SectionTitle,
  Title,
  ValueText,
  Wrapper,
} from "../../Properties/Details/styles";
import Dropdown from "../../../designComponents/Dropdown/Dropdown";
interface FileDetails {
  url: string;
  size: number;
  sizeType: "mb" | "kb";
}

interface ReportedByUser {
  _id: string;
  address: string;
  avatar?: string;
  email: string;
  firstName: string;
  fullName: string;
  lastName: string;
  phone: string;
}

interface ReportedToDetails {
  _id: string;
  avatar: string;
  createdAt: string;
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  role: "ADMIN" | "USER"; // Assuming roles could be user or admin
  updatedAt: string;
  __v: number;
}

interface ResourceDetails {
  _id: string;
  landlord: string;
  address: string;
  bookings: Array<any>;
  status: "ACTIVE" | "INACTIVE";
  // Add more properties if there are any specific details in the resource
}

interface Report {
  comments: Array<any>; // Assuming it's an array of comment objects, please define if needed
  createdAt: string;
  description: string;
  files: FileDetails[];
  priority: "High" | "Medium" | "Low";
  reportedBy: string; // User ID of the reporter
  reportedByUser: ReportedByUser;
  reportedResourceId: string;
  reportedResourceModel: "User" | "Property" | "Booking" | "ManualContract";
  reportedTo: string; // User ID of the resource owner or admin
  reportedToDetails: ReportedToDetails;
  reportedToModel: "Admin" | "User";
  resource: ResourceDetails;
  status: "Open" | "Closed";
  updatedAt: string;
  isReportToAdmin?: boolean;
  __v: number;
  _id: string;
  statusMessage: string; // Assuming status refers to success
}

const STATUS_ARRAY = [
  {
    label: "Open",
    value: "Open",
  },
  {
    label: "Acknowledged",
    value: "Acknowledged",
  },
  {
    label: "InProgress",
    value: "InProgress",
  },
  {
    label: "Resolved",
    value: "Resolved",
  },
  {
    label: "Closed",
    value: "Closed",
  },
];

function Report() {
  const { id } = useParams();
  const { auth } = useRoot();
  const [openDeleteModal, setOpenDeleteModal] = useState<string | null>(null);
  const [reportDetails, setReportDetails] = useState<Report | null>(null); // Initialize as null
  const [showCommentBox, toggleCommentBox] = useState<boolean>(false);
  const [statusState, setStatusState] = useState<
    "Open" | "Acknowledged" | "InProgress" | "Resolved" | "Closed"
  >();

  const changeReportStatus = async (
    status: "Open" | "Acknowledged" | "InProgress" | "Resolved" | "Closed"
  ) => {
    try {
      await requestAPI(reports.changeReportStatus(id || "", status)).then(
        async () => {
          await getTheDetails(id || "");
          toast.success("Successfully updated status !");
        }
      );
    } catch (error: unknown) {
      console.log(error);
    }
  };
  const getTheDetails = async (id: string) => {
    try {
      const response = await requestAPI(reports.getSingleReport(id));
      setReportDetails(response);
      setStatusState(response.status);
    } catch (error) {
      console.error("Failed to fetch report details", error);
    }
  };
  const [comment, setComment] = useState<{
    comment: string;
    commentId: string;
  }>({
    comment: "",
    commentId: "",
  });
  const navigate = useNavigate();
  useEffect(() => {
    if (id) {
      getTheDetails(id);
    }
  }, [id]);

  if (reportDetails === null) {
    return (
      <Wrapper>
        <ValueText>Loading</ValueText>
      </Wrapper>
    );
  }
  const images = reportDetails.files.map((item, index) => {
    return item.url;
  });
  const handleAddComment = async () => {
    if (id) {
      try {
        await requestAPI(
          reports.postComment({
            id: id,
            data: comment.commentId
              ? {
                  comment: comment.comment,
                  commentId: comment.commentId,
                }
              : {
                  comment: comment.comment,
                },
          })
        );
        toast.success("Successfully Posted");
        setComment({
          comment: "",
          commentId: "",
        });
        getTheDetails(id);
        toggleCommentBox(false);
      } catch (error: any) {
        toast.error(error.message);
      }
    }
  };
  const handleDeleteComment = async (commentId: string) => {
    if (id) {
      console.log(commentId, "id");
      try {
        await requestAPI(
          reports.deleteComment({
            id: id,
            commentId: commentId,
          })
        );
        toast.success("Successfully Deleted");
        getTheDetails(id);
      } catch (error: any) {
        toast.error(error.message);
      }
    }
  };
  return (
    <Wrapper style={{ padding: 30 }}>
      <SectionTitle>
        <Title>Report</Title>
      </SectionTitle>
      <Card style={{ margin: 0 }}>
        <SectionTitle>Report Details</SectionTitle>
        <ButtonsSection>
          <LabelText>Reported ID</LabelText>
          <ValueText>#{reportDetails._id}</ValueText>
        </ButtonsSection>
        <ButtonsSection>
          <LabelText>Reported By</LabelText>
          <ValueText>
            {renderFullDetails({
              avatar: reportDetails.reportedByUser?.avatar,
              email: reportDetails.reportedByUser?.email,
              firstName: reportDetails.reportedByUser?.firstName,
              lastName: reportDetails.reportedByUser?.lastName,
            })}
          </ValueText>
        </ButtonsSection>
        {reportDetails.reportedTo && (
          <ButtonsSection>
            <LabelText>Reported To</LabelText>
            <ValueText>
              {renderFullDetails({
                avatar: reportDetails.reportedToDetails?.avatar,
                email: reportDetails.reportedToDetails?.email,
                firstName: reportDetails.reportedToDetails?.firstName,
                lastName: reportDetails.reportedToDetails?.lastName,
              })}
            </ValueText>
          </ButtonsSection>
        )}
        <ButtonsSection>
          <LabelText>Priority</LabelText>
          <ValueText>{reportDetails.priority}</ValueText>
        </ButtonsSection>
        <ButtonsSection>
          <LabelText>Status</LabelText>
          <ValueText>{renderstatus(reportDetails.status)}</ValueText>
        </ButtonsSection>
      </Card>
      <Card style={{ margin: 0 }}>
        <SectionTitle>Description</SectionTitle>
        <ValueText>{reportDetails.description}</ValueText>
      </Card>
      {reportDetails?.status !== "Closed" && (
        <Card style={{ margin: 0 }}>
          <SectionTitle>Change Status</SectionTitle>
          <Dropdown
            enableSearch
            onSelect={(val) => {
              setStatusState(val as any);
            }}
            options={STATUS_ARRAY}
            value={statusState}
            name={statusState}
            style={{
              container: {
                width: "100%",
              },
            }}
            placeHolder="Choose Status"
          />
          {statusState !== reportDetails?.status && (
            <AcceptButton
              style={{ width: "140px", alignSelf: "flex-end" }}
              onClick={async () => await changeReportStatus(statusState!)}
            >
              Change Status
            </AcceptButton>
          )}
        </Card>
      )}
      <Card style={{ margin: 0 }}>
        <ButtonsSection>
          <SectionTitle>Resource Details</SectionTitle>
          {reportDetails?.reportedResourceId && (
            <AcceptButton
              onClick={() => {
                navigate(`/properties/${reportDetails?.reportedResourceId}`);
              }}
            >
              View
            </AcceptButton>
          )}
        </ButtonsSection>
        <ButtonsSection>
          <LabelText>Resource Type</LabelText>
          <ValueText> {reportDetails.reportedResourceModel} </ValueText>
        </ButtonsSection>
        <ButtonsSection>
          <LabelText>Resource Name</LabelText>
          <ValueText> {reportDetails?.resource?.address} </ValueText>
        </ButtonsSection>
        <ButtonsSection>
          <LabelText>Description</LabelText>
          <ValueText>{reportDetails?.description}</ValueText>
        </ButtonsSection>
        <ButtonsSection>
          <SectionTitle>Comments</SectionTitle>
          {reportDetails?.reportedToModel === "Admin" && (
            <AcceptButton
              onClick={() => {
                setComment({ comment: "", commentId: "" });
                toggleCommentBox(true);
              }}
            >
              Add Comment
            </AcceptButton>
          )}
        </ButtonsSection>
        {reportDetails.comments.length === 0 ? (
          <div style={{ display: "flex" }}>
            <LabelText>No Comments yet</LabelText>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              gap: 20,
            }}
          >
            {reportDetails.comments
              .map((item, index) => {
                return (
                  <div
                    key={item._id + index}
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "space-between",
                      backgroundColor:
                        auth && auth._id === item.commentedBy
                          ? undefined
                          : "#fffdf8",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "flex-start",
                        gap: 15,
                        justifyContent: "space-between",
                      }}
                    >
                      <img
                        src={
                          resizeImage(
                            item.commentedByDetails?.avatar ?? "",
                            "sm"
                          ) ||
                          "https://e7.pngegg.com/pngimages/84/165/png-clipart-united-states-avatar-organization-information-user-avatar-service-computer-wallpaper-thumbnail.png"
                        }
                        width={50}
                        height={50}
                        style={{ borderRadius: "50%" }}
                        alt="avatar"
                      />

                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <Typography
                          fontFamily="urbanist"
                          style={{
                            color: "#5F5F5F",
                            fontSize: 16,
                            fontWeight: 500,
                          }}
                        >
                          {item.commentedByDetails?.firstName &&
                          item.commentedByDetails?.lastName
                            ? `${textHelper.capitalizeFirstLetter(
                                item.commentedByDetails?.firstName
                              )} ${textHelper.capitalizeFirstLetter(
                                item.commentedByDetails?.lastName
                              )}`
                            : "Unknown User"}{" "}
                        </Typography>
                        <ValueText>{item.comment}</ValueText>
                        <LabelText style={{ fontSize: 10 }}>
                          {item.createdAt
                            ? humanReadableDateWithTime(item.createdAt)
                            : "No Comments"}
                        </LabelText>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 50 }}>
                      <div
                        style={{ display: "flex", gap: 10, cursor: "pointer" }}
                      >
                        {auth && auth._id === item.commentedBy && (
                          <>
                            <Delete
                              fill="#dd2edd"
                              onClick={() => {
                                setOpenDeleteModal(item._id);
                              }}
                            ></Delete>

                            <Edit
                              fill="#7E7E7E"
                              onClick={() => {
                                setComment({
                                  comment: item.comment,
                                  commentId: item._id,
                                });
                                toggleCommentBox(true);
                              }}
                            ></Edit>
                          </>
                        )}
                      </div>
                      {openDeleteModal === item._id && (
                        <DeleteModal
                          title={`Are you sure you want to delete this comment ?`}
                          loading={false}
                          isOpen={true}
                          onClose={() => {
                            setOpenDeleteModal(null);
                          }}
                          itemName="Comment"
                          onDelete={() => {
                            handleDeleteComment(item._id || "");
                          }}
                        ></DeleteModal>
                      )}
                    </div>
                  </div>
                );
              })
              .reverse()}
            {showCommentBox && (
              <div>
                <div>
                  <LabelInputField
                    name="comment"
                    label="Comment :"
                    value={comment.comment}
                    onChange={(e) => {
                      setComment({
                        comment: e.target.value,
                        commentId: comment.commentId,
                      });
                    }}
                    style={{
                      label: {
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#555A77",
                      },
                      input: {
                        height: 50,
                        border: "1px solid #F1EFFB",
                        padding: 8,
                        background: "#F1EFFB",
                        borderRadius: 6,
                        width: "100%",
                      },
                      container: {
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                        paddingTop: 14,
                        width: "100%", // Ensure input takes full width inside its container
                      },
                    }}
                  ></LabelInputField>
                </div>
                <div>
                  <ButtonsSection style={{ paddingTop: 20 }}>
                    <RejectButton
                      onClick={() => {
                        toggleCommentBox(false);
                      }}
                    >
                      Close
                    </RejectButton>
                    <AcceptButton
                      onClick={() => {
                        handleAddComment();
                      }}
                    >
                      Post
                    </AcceptButton>
                  </ButtonsSection>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>
      <Card style={{ margin: 0 }}>
        <SectionTitle>Attached Files</SectionTitle>
        <div style={{ display: "flex", gap: 20 }}>
          <RenderImages resize displayImage images={images} />
        </div>
      </Card>
    </Wrapper>
  );
}

export default Report;
